import axios from 'axios'
import { refreshAccessToken } from '@/utils/auth'
import config from '@/config'
import store from '@/store'

// Extend more headers the call under authRequired:
//
// headers: {
//   ...axios.defaults.headers,
//   "accept" : "application/json"
// },

const instance = axios.create({
  baseURL: config.backend.baseUrl,
  withCredentials: true,
})

// Add a request interceptor to handle the authRequired flag and token refresh
instance.interceptors.request.use(async (callConfig) => {
  if (callConfig.authRequired) {
    let accessToken = store.getters.getAccessToken

    if (isTokenExpired(accessToken)) {
      // Refresh the accessToken in the store using the /refresh endpoint
      await refreshAccessToken()

      // Refresh the accessToken from the store
      accessToken = store.getters.getAccessToken
    }
    // Add the refreshed or existing token to the request headers
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 [AUTH] Adding authorization header with token:', accessToken ? 'Token present' : 'No token')
    }
    callConfig.headers.Authorization = `AR-JWT ${accessToken}`
  }
  callConfig.headers['Content-Type'] = 'application/json;charset=utf-8'
  callConfig.headers['accept'] = 'application/json;charset=utf-8'
  return callConfig
})

// Add a response interceptor for error handling
instance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Check if the error is due to an expired token and we haven't already tried to refresh
    if (error.response?.status === 401 && 
        error.response?.data?.message?.includes('jwt expired') && 
        !originalRequest._retry &&
        originalRequest.authRequired) {
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 [AUTH] Token expired during request, refreshing and retrying...')
      }
      
      originalRequest._retry = true
      
      try {
        // Refresh the token
        await refreshAccessToken()
        
        // Get the new token
        const newAccessToken = store.getters.getAccessToken
        
        // Update the authorization header with the new token
        originalRequest.headers.Authorization = `AR-JWT ${newAccessToken}`
        
        // Retry the original request
        return instance(originalRequest)
      } catch (refreshError) {
        // If refresh fails, redirect to login
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ [AUTH] Token refresh failed:', refreshError)
        }
        // The refreshAccessToken function already handles redirect to login
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

// Helper function to check if a JWT token is expired or about to expire
function isTokenExpired(token) {
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const expirationTime = payload.exp * 1000 // Convert to milliseconds
    const currentTime = Date.now()
    
    // Consider token expired if it expires in less than 5 seconds
    // This gives us time to refresh before it actually expires
    const bufferTime = 5000 // 5 seconds
    
    return currentTime >= (expirationTime - bufferTime)
  } catch (e) {
    console.error('Failed to parse token:', e)
    return true
  }
}

export default instance
